import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PlanCardProps {
  id: string;
  name: string;
  price: number;
  period: 'monthly' | 'yearly';
  features: string[];
  recommended?: boolean;
  currentPlan?: boolean;
  onSelect: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export function PlanCard({
  name,
  price,
  period,
  features,
  recommended,
  currentPlan,
  onSelect,
  loading,
  disabled,
}: PlanCardProps) {
  const isYearly = period === 'yearly';

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-card border bg-card p-6 transition-shadow hover:shadow-lg',
        recommended && 'border-sapling-primary-100 ring-2 ring-sapling-primary-100',
        currentPlan && 'border-sapling-primary-200 bg-sapling-primary-100/5'
      )}
    >
      {recommended && !currentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sapling-primary-100 px-3 py-1 text-xs font-medium text-white">
          Recommended
        </div>
      )}

      {currentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sapling-primary-200 px-3 py-1 text-xs font-medium text-white">
          Current Plan
        </div>
      )}

      <h3 className="font-display text-xl font-semibold">{name}</h3>

      <div className="mt-4">
        <span className="font-display text-4xl font-bold">${price}</span>
        <span className="text-muted-foreground">/{isYearly ? 'year' : 'month'}</span>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className="h-5 w-5 shrink-0 text-sapling-primary-100" />
            <span className="text-sm text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        className="mt-6 w-full"
        variant={recommended && !currentPlan ? 'default' : 'outline'}
        onClick={onSelect}
        loading={loading}
        disabled={disabled || currentPlan}
      >
        {currentPlan ? 'Current Plan' : 'Select Plan'}
      </Button>
    </div>
  );
}
